import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { LoadingSpinner, Button } from 'components/common';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import apiAxios from 'helpers/apiAxios';
import { objectSerializer, formatNumber } from 'helpers/utils';
const PER_PAGE = 20;

// Workflow columns — a complete partition of the statuses (Approved is split
// into Pending Close / Closed so no waiver appears in two columns).
const COLUMNS = [
  { key: 'submited',         label: 'Submitted',            color: '#3b82f6' },
  { key: 'waiting_approval', label: 'Waiting for Approval', color: '#f59e0b' },
  { key: 'pending_closed',   label: 'Pending Close',        color: '#0ea5e9' },
  { key: 'closed',           label: 'Closed',               color: '#475569' },
  { key: 'rejected',         label: 'Rejected',             color: '#ef4444' },
  { key: 'canceled',         label: 'Canceled',             color: '#94a3b8' },
];

const fmtDate = (iso) => {
  if (!iso) return null;
  const [date] = String(iso).split('T');
  const [y, m, d] = date.split('-');
  const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${d}-${MONTHS[parseInt(m, 10) - 1]}-${y}`;
};

const emptyColumns = () => Object.fromEntries(
  COLUMNS.map((c) => [c.key, { rows: [], total: 0, page: 1, loading: true }])
);

const WaiverCard = ({ row, color }) => {
  const project = row.project || {};
  return (
    <div
      className="tw-bg-white tw-rounded tw-border tw-border-slate-200 tw-shadow-sm tw-p-2.5 hover:tw-shadow-md tw-transition-shadow"
      style={{ borderLeft: `3px solid ${color}` }}
    >
      <div className="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-mb-1">
        {project.id ? (
          <Link to={`/crm/projects/${project.id}/waivers/${row.id}`} state={{ source_waiver: true }}
            className="tw-font-mono tw-font-bold tw-text-xs tw-text-blue-700 hover:tw-underline">
            {row.waiver_number}
          </Link>
        ) : <span className="tw-font-mono tw-font-bold tw-text-xs tw-text-gray-700">{row.waiver_number}</span>}
        <span className="tw-font-mono tw-text-[10px] tw-text-gray-400">{project.j_number || project.q_number}</span>
      </div>

      <p className="tw-text-xs tw-text-gray-800 tw-font-medium tw-truncate tw-mb-1" title={project.name}>
        {project.name || '—'}
      </p>

      <div className="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-text-[11px]">
        <span className="tw-text-emerald-700 tw-font-semibold tw-tabular-nums">${formatNumber(row.waiver_value)}</span>
        {row.commitment_date && (
          <span className="tw-text-gray-400">⏱ {fmtDate(row.commitment_date)}</span>
        )}
      </div>

      {Array.isArray(row.cse_incharge) && row.cse_incharge.length > 0 && (
        <div className="tw-flex tw-flex-wrap tw-gap-1 tw-mt-1.5">
          {row.cse_incharge.map((name) => (
            <span key={name} className="tw-text-[10px] tw-leading-none tw-px-1.5 tw-py-1 tw-rounded-xl tw-bg-primary-50 tw-text-primary-600 tw-font-semibold">
              {name}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};
WaiverCard.propTypes = { row: PropTypes.object.isRequired, color: PropTypes.string };

// Status board — every column queries the shared list endpoint with its own
// status_scope, honouring the page-level criteria (search / dates / units /
// advanced filters). Columns paginate independently via "Load more".
const WaiverKanbanBoard = ({ criteria, counts }) => {
  const [columns, setColumns] = useState(emptyColumns);
  // The parent hands a new criteria object on every tick even when nothing changed;
  // key on its content so columns refetch only on a real criteria change.
  const criteriaKey = JSON.stringify(criteria);
  const stableCriteria = useMemo(() => JSON.parse(criteriaKey), [criteriaKey]);

  const fetchColumn = useCallback(async (key, page, append = false) => {
    setColumns((prev) => ({ ...prev, [key]: { ...prev[key], loading: true } }));
    try {
      const { data } = await apiAxios.get('/crm/projects/waivers', {
        params: {
          ...stableCriteria,
          status_scope: key,
          page,
          per_page: PER_PAGE,
          sort: { attribute: 'id', direction: 'desc' },
        },
      });
      const rows = objectSerializer(data?.rows || []) || [];
      setColumns((prev) => ({
        ...prev,
        [key]: {
          rows: append ? [...prev[key].rows, ...rows] : rows,
          total: data?.total_rows || 0,
          page,
          loading: false,
        },
      }));
    } catch {
      setColumns((prev) => ({ ...prev, [key]: { ...prev[key], loading: false } }));
    }
  }, [stableCriteria]);

  // Refetch every column whenever the page-level criteria change
  useEffect(() => {
    setColumns(emptyColumns());
    COLUMNS.forEach((c) => fetchColumn(c.key, 1));
  }, [fetchColumn]);

  return (
    <div className="tw-flex tw-gap-3 tw-overflow-x-auto tw-pb-2 tw-items-start">
      {COLUMNS.map((col) => {
        const state = columns[col.key];
        const count = counts?.[col.key] ?? state.total;
        const hasMore = state.rows.length < state.total;
        return (
          <div key={col.key} className="tw-w-[290px] tw-flex-shrink-0 tw-bg-slate-100/80 tw-rounded-lg tw-border tw-border-slate-200">
            {/* Column header */}
            <div className="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-2.5 tw-border-b tw-border-slate-200">
              <span className="tw-w-2 tw-h-2 tw-rounded-xl tw-flex-shrink-0" style={{ background: col.color }} />
              <span className="tw-text-xs tw-font-bold tw-text-slate-700 tw-uppercase tw-tracking-wide">{col.label}</span>
              <span className="tw-ml-auto tw-inline-flex tw-items-center tw-justify-center tw-min-w-[22px] tw-h-[18px] tw-px-1 tw-rounded-xl tw-bg-white tw-border tw-border-slate-200 tw-text-[10px] tw-font-bold tw-text-slate-500">
                {Number(count).toLocaleString()}
              </span>
            </div>

            {/* Cards */}
            <div className="tw-p-2 tw-space-y-2 tw-overflow-y-auto" style={{ maxHeight: 'max(240px, calc(100dvh - 330px))' }}>
              {state.loading && state.rows.length === 0 ? (
                <div className="tw-flex tw-justify-center tw-py-6"><LoadingSpinner size="sm" /></div>
              ) : state.rows.length === 0 ? (
                <p className="tw-text-[11px] tw-text-slate-400 tw-text-center tw-py-6 tw-mb-0">No waivers</p>
              ) : (
                <>
                  {state.rows.map((row) => <WaiverCard key={row.id} row={row} color={col.color} />)}
                  {hasMore && (
                    <Button
                      variant="secondary"
                      size="xs"
                      fullWidth
                      disabled={state.loading}
                      onClick={() => fetchColumn(col.key, state.page + 1, true)}
                      className="tw-text-primary-600 tw-border-primary-200 hover:tw-bg-primary-50"
                    >
                      {state.loading ? 'Loading…' : `Load more (${state.rows.length}/${state.total})`}
                    </Button>
                  )}
                </>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

WaiverKanbanBoard.propTypes = {
  criteria: PropTypes.object,
  counts: PropTypes.object,
};
WaiverKanbanBoard.defaultProps = { criteria: {}, counts: null };

export default WaiverKanbanBoard;
